/**
 * Shared-device data guard.
 *
 * Field crews share tablets, so on-device state left by one technician must not reach
 * the next. This module holds the DECISION on its own — free of React and Firebase — so
 * it can be unit-tested directly. Living inside AuthContext made it untestable in
 * practice (importing that module pulls in the whole Firebase client), and an untestable
 * decision is exactly how the bug below survived.
 */

/**
 * Should a sign-in wipe the previous user's on-device data?
 *
 * This is the piece that actually failed. `logout()` used to remove the last-uid marker
 * on the reasoning that it had "already wiped" — true of the Firestore cache, false of
 * locally autosaved form drafts. With the marker gone the next sign-in saw
 * `lastUid = null`, concluded "no user change", and skipped the clear. So technician A
 * tapping Log Out at shift end — the trained action, not an edge case — left A's
 * in-progress report in front of technician B.
 *
 * True only on a real CHANGE of user:
 *   - first-ever sign-in has nothing to clear;
 *   - a same-user return must NOT clear, because local form drafts have no Firestore
 *     copy and wiping them is irreversible work loss.
 */
export function shouldClearPreviousUserData(
  lastUid: string | null | undefined,
  incomingUid: string
): boolean {
  return Boolean(lastUid && lastUid !== incomingUid);
}
